<template>
  <div>
    <v-card
      v-if="hasItlModule"
      class="py-2 px-5 rounded-lg fill-height"
    >
      <v-card-text>
        <v-row no-gutters>
          <v-col cols="3">
            <v-row>
              <v-col cols="auto">
                <v-avatar
                  size="100px"
                  class="elevation-1"
                >
                  <v-img
                    :src="player.photo_url"
                    width="100px"
                    height="100px"
                    class="rounded"
                  >
                    <template v-slot:placeholder>
                      <v-icon x-large>
                        fal fa-user
                      </v-icon>
                    </template>
                  </v-img>
                </v-avatar>
              </v-col>
              <v-col>
                <v-row
                  align="center"
                  class="title flex-column"
                >
                  <v-col class="grey--text text--darken-4 d-flex flex-column">
                    <span class="font-weight-bold">
                      {{ player.last_name }}
                    </span>
                    <span class="subtitle-2">
                      {{ player.first_name }}
                    </span>
                  </v-col>
                  <v-col class="subtitle-2 d-flex flex-column">
                    <div>
                      {{ $t('individual_analysis.data.team') }}: {{ team.name }}
                    </div>
                    <div class="mt-2">
                      {{ $t('individual_analysis.data.position') }}:
                      <v-chip
                        label
                        small
                      >
                        {{ playerPositions }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="pt-3">
              <v-col>
                <v-btn
                  class="text-none"
                  color="primary"
                  outlined
                  small
                  @click="eventPlayerDialog = true"
                >
                  {{ $t('button.event_player_details') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="eventParentPlayerStats.UXPCSTDR">
              <v-col>
                <div>
                  {{ $t('button.sensor_data_duration') }}: <span class="font-weight-medium">{{ eventParentPlayerStats.UXPCSTDR }}</span> min
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col class="mx-4">
            <v-row class="fill-height">
              <v-col
                v-for="variable in itlVariables"
                :key="variable.value"
                cols="6"
                align-self="center"
              >
                <v-card
                  rounded="lg"
                  outlined
                  :color="variable.colorable && eventParentPlayerStats[variable.value] ? getColorForValue(eventParentPlayerStats[variable.value], variable.max, variable.reverse) : 'grey lighten-2'"
                >
                  <v-card-text class="d-flex flex-column align-center text-center py-5 px-1">
                    <span class="text-h6 font-weight-bold grey--text text--grey-100">{{ eventParentPlayerStats[variable.value] ? eventParentPlayerStats[variable.value] : '-' }}</span>
                    <span class="mt-4 text-body-1 font-weight-medium grey--text text--grey-100">{{ variable.text }}</span>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            cols="auto"
            class="d-flex align-center justify-center"
          >
            <human-body-female-front-muscles
              v-if="player.gender === 'female'"
              style="height: 200px; width: 120px"
              :stats="player.player_stats"
            />
            <human-body-front-muscles
              v-else
              style="height: 200px; width: 120px"
              :stats="player.player_stats"
            />
            <human-body-female-back-muscles
              v-if="player.gender === 'female'"
              style="height: 200px; width: 120px"
              :stats="player.player_stats"
            />
            <human-body-back-muscles
              v-else
              style="height: 200px; width: 120px"
              :stats="player.player_stats"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card
      v-else
      class="py-2 px-5 rounded-lg fill-height"
    >
      <v-row align="center">
        <v-col cols="auto">
          <v-avatar
            size="50px"
            class="elevation-1"
          >
            <v-img
              :src="player.photo_url"
              width="50px"
              height="50px"
              class="rounded"
            >
              <template v-slot:placeholder>
                <v-icon>
                  fal fa-user
                </v-icon>
              </template>
            </v-img>
          </v-avatar>
        </v-col>
        <v-col>
          <div class="font-weight-bold">
            {{ player.last_name }} {{ player.first_name }}
            <v-chip
              class="ml-2"
              label
              small
            >
              {{ playerPositions }}
            </v-chip>
          </div>
          <div>
            {{ $t('individual_analysis.data.team') }}: {{ team.name }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-btn
            class="text-none"
            color="primary"
            outlined
            small
            @click="eventPlayerDialog = true"
          >
            {{ $t('button.event_player_details') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <event-player-dialog
      v-if="event && player"
      :visible.sync="eventPlayerDialog"
      :player-id="player.id"
      :event="event.data"
    />
  </div>
</template>

<script>
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import EventPlayerDialog from '@/components/dialogs/EventPlayerDialog.vue'
import { getColorForValue, toCamelCase } from '@/utils/helpers'

export default {
  name: 'PerformancePlayerInfoCard',
  components: {
    HumanBodyFemaleFrontMuscles,
    HumanBodyBackMuscles,
    HumanBodyFemaleBackMuscles,
    HumanBodyFrontMuscles,
    EventPlayerDialog
  },
  props: {
    player: {
      type: Object,
      required: true
    },
    team: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    referenceStats: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      eventPlayerDialog: false
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasItlModule() {
      return this.user.organisation_settings?.has_itl_module
    },
    eventParentPlayerStats() {
      if (Object.keys(this.referenceStats).length > 0) return this.referenceStats

      return this.event.data.players.find(player => player.id === this.player.id)?.player_stats || {}
    },
    playerPositions() {
      return this.player.player_positions && this.player.player_positions.length > 0
        ? this.player.player_positions.map(p => this.$t(`player_position.${toCamelCase(p.name)}.short`)).join(', ')
        : '-'
    },
    itlVariables() {
      return [
        {
          value: 'TTM',
          text: this.$t('variables.TTM'),
          colorable: false,
          max: null,
          reverse: null
        },
        {
          value: 'TG',
          text: this.$t('variables.TG'),
          colorable: true,
          max: 10,
          reverse: true
        },
        {
          value: 'TL',
          text: this.$t('variables.TL'),
          colorable: false,
          max: null,
          reverse: null
        },
        {
          value: 'ENJY',
          text: this.$t('variables.ENJY'),
          colorable: true,
          max: 5,
          reverse: false
        }
      ]
    }
  },
  methods: {
    getColorForValue,
    toCamelCase
  }
}
</script>
